import React, { Component } from 'react';
import { styled } from '@compiled/react';
import Button, { type Appearance } from '@atlaskit/button';
import { ModalFooter, ModalBody } from '@atlaskit/modal-dialog';
import { token } from '@atlaskit/tokens';
import { JiraModal as ModalDialog } from '@atlassian/jira-modal/src/ui/jira-modal.tsx';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { successFlag, errorFlag, deleteSuccessFlag } from '../flags/index.tsx';
import deleteShortcut from '../services/delete-shortcut/index.tsx';
import editShortcut from '../services/edit-shortcut/index.tsx';
import type { ViewProps, ViewState, OnDataChange } from '../types.tsx';
import messages from './messages.tsx';

// eslint-disable-next-line jira/react/no-class-components
class EditShortcutModal extends Component<ViewProps, ViewState> {
	constructor(props: ViewProps) {
		// @ts-expect-error - TS2554 - Expected 1-2 arguments, but got 0.
		super();
		this.state = {
			url: props.item.href || '',
			name: props.item.text || '',
			hasError: false,
		};
	}

	onClose = () => {
		const { onCompleted } = this.props;
		onCompleted({ item: undefined, deleted: false });
	};

	onEdit = () => {
		this.performEdit();
	};

	onDelete = () => {
		this.performDelete();
	};

	onDataChange: OnDataChange = ({ url, name, hasError }) => {
		this.setState({ url, name, hasError });
	};

	async performEdit() {
		const { projectId, baseUrl, onCompleted, flagService, item } = this.props;
		const { name, url } = this.state;
		try {
			await editShortcut(baseUrl, projectId, {
				id: item.id,
				name,
				url,
				type: item.shortcutType || '',
				icon: '',
			});
			const editedItem = {
				...item,
				href: url,
				text: name,
			};
			onCompleted({ item: editedItem, deleted: false });
			flagService.showFlag(successFlag());
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (e: any) {
			flagService.showFlag(errorFlag());
			onCompleted({ item: undefined, deleted: false });
		}
	}

	async performDelete() {
		const { projectId, baseUrl, onCompleted, flagService, item } = this.props;
		try {
			await deleteShortcut(baseUrl, projectId, item.id);
			flagService.showFlag(deleteSuccessFlag());
			onCompleted({ item, deleted: true });
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (e: any) {
			flagService.showFlag(errorFlag());
			onCompleted({ item: undefined, deleted: false });
		}
	}

	render() {
		const {
			item,
			Content,
			Header,
			intl: { formatMessage },
			editButtonRef,
		} = this.props;
		const { name, url, hasError } = this.state;
		const qualityProps = {
			width: 'medium',
		};
		const actions = [
			{
				appearance: 'subtle',
				onClick: this.onDelete,
				text: formatMessage(messages.removeShortcut),
			},
			{
				appearance: 'link',
				onClick: this.onClose,
				text: formatMessage(messages.cancelButton),
			},
			{
				appearance: 'primary',
				onClick: this.onEdit,
				text: formatMessage(messages.editButton),
				isDisabled: hasError,
			},
		];

		return (
			<ShortcutScope>
				<ModalDialog
					messageId="navigation-shortcuts.edit-shortcut-modal.modal-dialog"
					messageType="transactional"
					{...qualityProps}
					autoFocus
					onClose={this.onClose}
					shouldReturnFocus={editButtonRef}
					shouldCloseOnOverlayClick={false}
				>
					<Header />
					<ModalBody>
						<ContentWrapper>
							<Heading>{formatMessage(messages.editShortcut)}</Heading>
							<Content item={item} name={name} url={url} onDataChange={this.onDataChange} />
						</ContentWrapper>
					</ModalBody>
					<ModalFooter>
						{actions.map((action) => (
							<Button
								key={`btn-${action.appearance}`}
								// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
								appearance={action.appearance as Appearance}
								onClick={action.onClick}
							>
								{action.text}
							</Button>
						))}
					</ModalFooter>
				</ModalDialog>
			</ShortcutScope>
		);
	}
}

export default EditShortcutModal;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Heading = styled.h1({
	marginTop: 0,
});

// eslint-disable-next-line @atlaskit/design-system/use-primitives, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentWrapper = styled.div({
	paddingTop: token('space.200'),
	paddingRight: token('space.300'),
	paddingBottom: token('space.200'),
	paddingLeft: token('space.300'),
});
